/**
 * SnapshotStatsReporter (2026-05-29) — periodically pushes the
 * SnapshotStatsTracker summary to the server via the `netobs:snapshotStats`
 * Colyseus message. Server caches per-session (SnapshotStatsBuffer) and
 * surfaces it via `GET /api/diag/snapshot-stats` for the inter-client
 * smoothness verdict.
 *
 * 5s cadence, gated on isNetobsEnabled() (the /debug surface) so play
 * sessions don't push when nothing's reading. Mirrors FrameJitterReporter.
 *
 * Default-OFF: SnapshotStatsTracker.enabled is false unless ?snapstats=1 or
 * the rig flips it. The server skips empty snapshots (no samples yet).
 */

import React, { useEffect } from 'react';
import { isNetobsEnabled } from './isNetobsEnabled';
import { SnapshotStatsTracker } from '../../diagnostics/SnapshotStatsTracker';

const REPORT_INTERVAL_MS = 5000;

function sendSnapshot(): void {
  const room = (globalThis as any).nm?.getRoom?.();
  if (!room || typeof room.send !== 'function') return;
  try {
    const snapshot = SnapshotStatsTracker.getSnapshot();
    // Skip empty snapshots (tracker disabled or no interpolator activity yet).
    const hasData = snapshot.perType.some((p) => p.sampleCount > 0 || p.pushCount > 0);
    if (!hasData) return;
    room.send('netobs:snapshotStats', snapshot);
  } catch {
    // diagnostic-only — never gameplay
  }
}

const SnapshotStatsReporterInner: React.FC = () => {
  useEffect(() => {
    const id = setInterval(sendSnapshot, REPORT_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  return null;
};

export const SnapshotStatsReporter: React.FC = () => {
  if (!isNetobsEnabled()) return null;
  return <SnapshotStatsReporterInner />;
};
